import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "./components/Header";
import Login from "./components/auth/Login";
import AuthCallback from "./components/auth/AuthCallback";
import Dashboard from "./components/Dashboard";
import Admin from "./components/Admin";
import ContactPage from "./components/ContactPage";
import PrivacyPolicyPage from "./components/PrivacyPolicyPage";
import TermsOfServicePage from "./components/TermsOfServicePage";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import DevBypass from "./components/auth/DevBypass";
import SeasonalEffects from "./components/SeasonalEffects";
import { AuthProvider } from "./contexts/AuthContext";
import NewsletterSignup from "./components/forms/NewsletterSignup";
import SignupModal from "./components/modals/SignupModal";
import Footer from "./components/Footer";
import { LocationSelector } from "./components/projects/LocationSelector";
import PropertyMap from "./components/map/PropertyMap";
import PrivateRoute from "./components/auth/PrivateRoute";
import "./App.css";

// Home page component
const Home: React.FC = () => {
  const { t } = useTranslation();
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [currentSeason, setCurrentSeason] = useState<string>("spring");
  const [lastAnnouncedSeason, setLastAnnouncedSeason] = useState<string>("");

  const openSignupModal = () => {
    setIsSignupModalOpen(true);
  };

  const closeSignupModal = () => {
    setIsSignupModalOpen(false);
  };

  // Seasonal descriptions for accessibility
  const seasonalDescriptions = {
    spring: t("seasonal.descriptions.spring", {
      defaultValue: "Spring season: Fresh greenery emerges with bright new leaves and delicate blossoms. Gentle rain showers nourish the growing plants and a warm sun begins to shine."
    }),
    summer: t("seasonal.descriptions.summer", {
      defaultValue: "Summer season: Lush, vibrant growth with deep greens and bright colors. The landscape is full and thriving in the warm sunshine."
    }),
    fall: t("seasonal.descriptions.fall", {
      defaultValue: "Fall season: Leaves begin to change to warm oranges and yellows. Gentle breezes carry them through the air as nature prepares for rest."
    }),
    winter: t("seasonal.descriptions.winter", {
      defaultValue: "Winter season: Cool blues and whites create a peaceful landscape. Snowflakes drift softly through the air as dormant plants rest, waiting for spring's return."
    }),
  };

  useEffect(() => {
    // Create ARIA live region for accessibility announcements
    const liveRegion = document.createElement("div");
    liveRegion.setAttribute("aria-live", "polite");
    liveRegion.setAttribute("aria-atomic", "true");
    liveRegion.className = "sr-only seasonal-announcer";
    liveRegion.style.position = "absolute";
    liveRegion.style.width = "1px";
    liveRegion.style.height = "1px";
    liveRegion.style.padding = "0";
    liveRegion.style.margin = "-1px";
    liveRegion.style.overflow = "hidden";
    liveRegion.style.clip = "rect(0, 0, 0, 0)";
    liveRegion.style.whiteSpace = "nowrap";
    liveRegion.style.border = "0";
    document.body.appendChild(liveRegion);

    // Create season indicator bar
    const seasonIndicator = document.createElement("div");
    seasonIndicator.className = "season-indicator";
    document.body.appendChild(seasonIndicator);

    // Handle scroll events to change season
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const totalHeight = document.body.scrollHeight - window.innerHeight;
      const scrollPercentage = scrollPosition / totalHeight;

      // Set different seasons based on scroll position
      let newSeason: string;

      if (scrollPercentage < 0.25) {
        newSeason = "spring";
        document.body.setAttribute("data-season", "spring");
        document.body.className = "season-spring";
      } else if (scrollPercentage < 0.5) {
        newSeason = "summer";
        document.body.setAttribute("data-season", "summer");
        document.body.className = "season-summer";
      } else if (scrollPercentage < 0.75) {
        newSeason = "fall";
        document.body.setAttribute("data-season", "fall");
        document.body.className = "season-fall";
      } else {
        newSeason = "winter";
        document.body.setAttribute("data-season", "winter");
        document.body.className = "season-winter";
      }

      // Only announce if season changed
      if (newSeason !== currentSeason) {
        setCurrentSeason(newSeason);
        announceSeasonChange(newSeason);
      }
    };

    // Function to announce season change for screen readers
    const announceSeasonChange = (newSeason: string) => {
      if (lastAnnouncedSeason !== newSeason) {
        const liveRegion = document.querySelector(".seasonal-announcer");
        if (liveRegion) {
          liveRegion.textContent =
            seasonalDescriptions[
              newSeason as keyof typeof seasonalDescriptions
            ];
          setLastAnnouncedSeason(newSeason);

          // Trigger haptic feedback on mobile if supported
          if (
            navigator.vibrate &&
            window.matchMedia("(max-width: 768px)").matches
          ) {
            switch (newSeason) {
              case "spring":
                navigator.vibrate([50, 100, 50]); // Light pattern for spring
                break;
              case "summer":
                navigator.vibrate([100, 50, 100]); // Stronger pattern for summer
                break;
              case "fall":
                navigator.vibrate([50, 100, 50, 100, 50]); // Rhythmic pattern for fall
                break;
              case "winter":
                navigator.vibrate([25, 50, 25, 50, 25, 50, 25]); // Gentle snowfall pattern
                break;
            }
          }
        }
      }
    };

    // Initial call to set the correct season
    handleScroll();

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Add keyboard navigation for seasonal sections
    const handleKeydown = (e: KeyboardEvent) => {
      // Allow pressing 1-4 to navigate between seasonal sections
      if (e.key >= "1" && e.key <= "4") {
        e.preventDefault();
        const sectionIndex = parseInt(e.key) - 1;
        const sections = document.querySelectorAll("section");
        if (sections[sectionIndex]) {
          sections[sectionIndex].scrollIntoView({ behavior: "smooth" });

          // Announce the appropriate season
          const seasonNames = ["spring", "summer", "fall", "winter"];
          announceSeasonChange(seasonNames[sectionIndex]);
        }
      }
    };

    window.addEventListener("keydown", handleKeydown);

    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("keydown", handleKeydown);

      if (document.querySelector(".seasonal-announcer")) {
        document.body.removeChild(
          document.querySelector(".seasonal-announcer") as Node
        );
      }

      if (document.querySelector(".season-indicator")) {
        document.body.removeChild(
          document.querySelector(".season-indicator") as Node
        );
      }
    };
  }, [currentSeason, lastAnnouncedSeason]);

  return (
    <div className="app">
      <Header />
      {/* Background Logo - using the bug logo (icon only) */}
      <div className="background-logo">
        <img
          src="/images/gesslar-gardens-logo-bug.png"
          alt="Gesslar Gardens Logo"
          className="background-logo-image"
        />
      </div>
      <main>
        {/* Seasonal transitions effect component */}
        <SeasonalEffects currentSeason={currentSeason} />

        {/* Accessibility notice */}
        <div className="accessibility-notice">
          <button
            className="info-button"
            aria-label={t("seasonal.accessibility.seasonalInfo", { defaultValue: "Seasonal experience information" })}
            title={t("seasonal.accessibility.learnAboutTransitions", { defaultValue: "Learn about seasonal transitions" })}
            onClick={() => {
              const dialog = document.getElementById("seasonal-info-dialog");
              if (dialog) {
                dialog.classList.toggle("show");
              }
            }}
          >
            <i className="fas fa-info-circle"></i>
          </button>
          <div
            id="seasonal-info-dialog"
            className="info-dialog"
            role="dialog"
            aria-labelledby="dialog-title"
          >
            <h3 id="dialog-title">{t("seasonal.accessibility.dialogTitle", { defaultValue: "Seasonal Experience" })}</h3>
            <p>
              {t("seasonal.accessibility.dialogDescription", { 
                defaultValue: "As you scroll through our site, you'll journey through the changing seasons of a garden year:" 
              })}
            </p>
            <ul className="seasonal-descriptions">
              <li>
                <strong>{t("seasonal.accessibility.springDescription", {
                  defaultValue: "Spring (Top): Fresh greenery emerges with bright new leaves and delicate blossoms. The air feels light with renewal and possibility."
                })}</strong>
              </li>
              <li>
                <strong>{t("seasonal.accessibility.summerDescription", {
                  defaultValue: "Summer: Lush, vibrant growth with deep greens and bright colors. The landscape is full and thriving in the warm sunshine."
                })}</strong>
              </li>
              <li>
                <strong>{t("seasonal.accessibility.fallDescription", {
                  defaultValue: "Fall: Leaves begin to change to warm oranges and yellows. Gentle breezes carry them through the air as nature prepares for rest."
                })}</strong>
              </li>
              <li>
                <strong>{t("seasonal.accessibility.winterDescription", {
                  defaultValue: "Winter (Bottom): Cool blues and whites create a peaceful landscape. Dormant plants rest under a light blanket of snow, waiting for spring's return."
                })}</strong>
              </li>
            </ul>
            <p>
              {t("seasonal.accessibility.gardenDreams", {
                defaultValue: "Your garden dreams evolve through all seasons with Gesslar Gardens."
              })}
            </p>
            <p>
              {t("seasonal.accessibility.keyboardNavigation", {
                defaultValue: "Keyboard users: Press 1-4 to navigate directly between seasonal sections."
              })}
            </p>
            <button
              className="close-button"
              aria-label={t("seasonal.accessibility.closeInfo", { defaultValue: "Close seasonal information" })}
              onClick={() => {
                const dialog = document.getElementById("seasonal-info-dialog");
                if (dialog) {
                  dialog.classList.remove("show");
                }
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        <section
          className="hero"
          id="spring-section"
          aria-label={t("seasonal.sections.spring", { defaultValue: "Spring Season: Visualize and Plan Projects" })}
        >
          <div className="container">
            <div className="hero-content">
              <h1>{t("hero.title")}</h1>
              <p>{t("hero.subtitle")}</p>
              <div className="cta-buttons">
                <button onClick={openSignupModal} className="btn btn-primary">
                  {t("hero.cta.primary")}
                </button>
                <a href="#how-it-works" className="btn btn-secondary">
                  {t("hero.cta.secondary")}
                </a>
              </div>
            </div>
          </div>
        </section>

        <section
          className="features"
          id="summer-section"
          aria-label={t("seasonal.sections.summer", { defaultValue: "Summer Season: Collaborate Seamlessly" })}
        >
          <div className="container">
            <h2>{t("features.title")}</h2>
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">
                  <i className="fas fa-ruler-combined"></i>
                </div>
                <h3>{t("features.design.title")}</h3>
                <p>{t("features.design.description")}</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <i className="fas fa-users"></i>
                </div>
                <h3>{t("features.maintenance.title")}</h3>
                <p>{t("features.maintenance.description")}</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <i className="fas fa-calendar-alt"></i>
                </div>
                <h3>{t("features.installation.title")}</h3>
                <p>{t("features.installation.description")}</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <i className="fas fa-file-invoice-dollar"></i>
                </div>
                <h3>{t("features.irrigation.title")}</h3>
                <p>{t("features.irrigation.description")}</p>
              </div>
            </div>
          </div>
        </section>

        <section
          className="call-to-action"
          id="fall-section"
          aria-label={t("seasonal.sections.fall", { defaultValue: "Fall Season: Get Early Access" })}
        >
          <div className="container">
            <div className="cta-content">
              <h2>{t("signup.title")}</h2>
              <p>{t("signup.subtitle")}</p>
              <button
                onClick={openSignupModal}
                className="btn btn-primary btn-large"
              >
                {t("signup.form.submit")}
              </button>
            </div>
          </div>
        </section>

        <section className="newsletter-section">
          <div className="container">
            <div className="newsletter-wrapper">
              <NewsletterSignup />
            </div>
          </div>
        </section>

        <section
          className="footer-wrapper"
          id="winter-section"
          aria-label={t("seasonal.sections.winter", { defaultValue: "Winter Season: Generate Quotes Instantly" })}
        >
          <Footer />
        </section>
      </main>

      {/* Modals */}
      <SignupModal isOpen={isSignupModalOpen} onClose={closeSignupModal} />
    </div>
  );
};

// Wrapper component to get URL params
const LocationSelectorWrapper = () => {
  const { projectId } = useParams();
  return <LocationSelector projectId={projectId || ""} />;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/admin" element={<Admin />} />
            {/* Add other protected routes here */}
          </Route>

          {/* New project and location selection routes */}
          <Route
            path="/projects/:projectId/location"
            element={
              <PrivateRoute>
                <LocationSelectorWrapper />
              </PrivateRoute>
            }
          />

          <Route
            path="/projects/:projectId/map"
            element={
              <PrivateRoute>
                <PropertyMap />
              </PrivateRoute>
            }
          />

          {/* Catch-all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <DevBypass />
      </Router>
    </AuthProvider>
  );
};

export default App;
